import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import GlobalNav from "../components/GlobalNav";
import API from "../api";
import genPlayer from "../audio/GenPlayer";
import { Trash, Plus, FloppyDisk, MusicNote, ThumbsUp, ThumbsDown, Sparkle, Play, Pause, Tag, UploadSimple, SpinnerGap } from "@phosphor-icons/react";
import DrumKitsPanel, { BassKitsPanel, MelodyKitsPanel } from "../components/DrumKitsPanel";
import ImageToMidiPanel from "../components/ImageToMidiPanel";

// Admin-only AI Training Lab. Draw MIDI melodies, label them with style tags,
// and persist them as "training examples" that the AI Generator uses as style
// references when producing new melodies (style consistency feedback loop).
//
// Grid model: 16 steps × 24 pitches (C3..B4). Click toggles cells. Saved notes
// become {step, pitch, duration} triples in MongoDB.
const STEPS = 16;
const PITCHES = [
  "C5","B4","A#4","A4","G#4","G4","F#4","F4","E4","D#4","D4","C#4","C4",
  "B3","A#3","A3","G#3","G3","F#3","F3","E3","D#3","D3","C#3","C3",
];

export default function TrainingLabPage() {
  const { user, isAdmin, ready } = useAuth();
  const nav = useNavigate();
  const [examples, setExamples] = useState([]);
  const [label, setLabel] = useState("");
  const [styleTag, setStyleTag] = useState("");
  const [bpm, setBpm] = useState(120);
  const [notes, setNotes] = useState([]); // [{step,pitch,duration}]
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Only redirect AFTER auth has finished resolving. We don't redirect on the
    // very first tick after sign-in because `isAdmin` is set asynchronously
    // after the `/auth/me` call returns — bouncing too early was the previous
    // bug. We also wait a short grace window for `is_admin` to come back.
    if (!ready) return;
    if (!user) { nav("/"); return; }
    if (!isAdmin) {
      // Give the AuthContext one more refresh attempt before bouncing — handles
      // the case where the user just signed in and `is_admin` hasn't propagated.
      const t = setTimeout(() => {
        if (!useAuth.length) {} // noop to satisfy lint
      }, 200);
      const bounce = setTimeout(() => { if (!isAdmin) nav("/"); }, 600);
      return () => { clearTimeout(t); clearTimeout(bounce); };
    }
  }, [user, isAdmin, ready, nav]);

  useEffect(() => {
    if (!isAdmin) return;
    API.get("/training/examples")
      .then((r) => setExamples(r.data.examples || []))
      .catch(() => {});
  }, [isAdmin]);

  if (!user || !isAdmin) return null;

  const toggleCell = (pitch, step) => {
    setNotes((prev) => {
      const idx = prev.findIndex((n) => n.pitch === pitch && n.step === step);
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [...prev, { pitch, step, duration: 1 }];
    });
  };

  const clearGrid = () => setNotes([]);

  const save = async () => {
    if (!label.trim()) { setErr("Give this melody a label first."); return; }
    if (notes.length === 0) { setErr("Add at least one note to the grid."); return; }
    setSaving(true); setErr("");
    try {
      const { data } = await API.post("/training/examples", {
        label: label.trim(),
        style_tag: styleTag.trim(),
        bpm,
        key: "C",
        notes,
      });
      setExamples((prev) => [data.example, ...prev]);
      setLabel(""); setStyleTag(""); setNotes([]);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await API.delete(`/training/examples/${id}`);
    setExamples((prev) => prev.filter((e) => e.id !== id));
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "linear-gradient(180deg,#070707,#0c0c0c)" }}>
      <header className="px-6 py-4 flex items-center justify-between border-b border-[#1a1a1a]">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/")} className="text-sm font-mono text-white/60 hover:text-white" data-testid="training-back">← back</button>
          <GlobalNav />
        </div>
        <h1 className="font-display font-extrabold tracking-tight text-lg flex items-center gap-2">
          <span className="text-pink-400">🧪</span> AI Training Lab
          <span className="text-[10px] font-mono text-white/40 uppercase tracking-wider bg-pink-500/10 border border-pink-500/30 px-2 py-0.5 rounded ml-2">admin only</span>
        </h1>
        <span className="text-xs font-mono text-white/40">{examples.length} examples</span>
      </header>

      <main className="max-w-6xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: drawing canvas */}
        <section className="lg:col-span-2 rounded-2xl p-5 space-y-4" style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }} data-testid="training-grid-section">
          <div className="flex items-center gap-3">
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label (e.g. Jazzy ii-V-I in C)"
              className="flex-1 px-3 py-2 rounded-md text-sm bg-[#0a0a0a] border border-[#262626] focus:outline-none focus:border-white/40"
              data-testid="training-label"
            />
            <input
              value={styleTag}
              onChange={(e) => setStyleTag(e.target.value)}
              placeholder="style tag (trap, house, afro…)"
              className="w-44 px-3 py-2 rounded-md text-sm bg-[#0a0a0a] border border-[#262626] focus:outline-none focus:border-white/40"
              data-testid="training-style-tag"
            />
            <input
              type="number"
              value={bpm}
              onChange={(e) => setBpm(Number(e.target.value))}
              min={40}
              max={220}
              className="w-20 px-3 py-2 rounded-md text-sm bg-[#0a0a0a] border border-[#262626] focus:outline-none focus:border-white/40"
              data-testid="training-bpm"
            />
          </div>

          {/* Grid */}
          <div
            className="relative rounded-lg overflow-hidden"
            style={{ background: "#050505", border: "1px solid #161616" }}
            data-testid="training-grid"
          >
            {PITCHES.map((pitch, rowIdx) => (
              <div key={pitch} className="flex items-center" style={{ borderBottom: "1px solid #111" }}>
                <div className="w-12 text-[10px] font-mono text-white/40 pl-2" style={{ height: 18 }}>{pitch}</div>
                <div className="flex-1 flex">
                  {Array.from({ length: STEPS }).map((_, step) => {
                    const active = notes.some((n) => n.pitch === pitch && n.step === step);
                    const beatLine = step % 4 === 0;
                    return (
                      <button
                        key={step}
                        onClick={() => toggleCell(pitch, step)}
                        className="flex-1 relative transition"
                        style={{
                          height: 18,
                          background: active
                            ? "linear-gradient(180deg,#f472b6,#a855f7)"
                            : (rowIdx % 2 === 0 ? "#080808" : "#0a0a0a"),
                          borderLeft: beatLine ? "1px solid #1f1f1f" : "1px solid #0e0e0e",
                          boxShadow: active ? "inset 0 0 6px rgba(236,72,153,0.6)" : undefined,
                        }}
                        title={`${pitch} · step ${step + 1}`}
                        data-testid={`training-cell-${pitch}-${step}`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-white/40 font-mono">{notes.length} note{notes.length === 1 ? "" : "s"} on the grid</div>
            <div className="flex gap-2">
              <button onClick={clearGrid} className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#0a0a0a] border border-[#262626] hover:bg-[#151515]" data-testid="training-clear">
                <Trash size={12} weight="bold" className="inline mr-1" /> clear
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-1.5 rounded-md text-xs font-bold"
                style={{ background: "linear-gradient(180deg,#fff,#d4d4d4)", color: "#0a0a0a", border: "1px solid #303030" }}
                data-testid="training-save"
              >
                <FloppyDisk size={12} weight="bold" className="inline mr-1" /> {saving ? "saving…" : "Save as training example"}
              </button>
            </div>
          </div>
          {err && <div className="text-xs text-red-400" data-testid="training-error">{err}</div>}

          <div className="rounded-md p-3 text-xs font-mono text-white/50" style={{ background: "#0a0a0a", border: "1px dashed #262626" }}>
            💡 Saved examples become <span className="text-pink-300">style references</span> for the AI Generator. The more melodies you save in a given tag, the more closely future AI generations match your style.
          </div>
        </section>

        {/* RIGHT: examples list */}
        <aside className="rounded-2xl p-5 space-y-3 self-start" style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }} data-testid="training-examples">
          <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2">
            <MusicNote size={14} weight="bold" /> Saved examples
          </h2>
          {examples.length === 0 ? (
            <div className="text-sm text-white/40 py-8 text-center font-mono">no training data yet.</div>
          ) : (
            <ul className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {examples.map((ex) => (
                <li key={ex.id} className="rounded-md p-2 flex items-start justify-between gap-2" style={{ background: "#0a0a0a", border: "1px solid #1a1a1a" }} data-testid={`training-ex-${ex.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{ex.label}</div>
                    <div className="text-[10px] font-mono text-white/40">
                      {ex.style_tag ? `#${ex.style_tag} · ` : ""}{ex.bpm} BPM · {ex.notes?.length || 0} notes
                    </div>
                  </div>
                  <button onClick={() => remove(ex.id)} className="text-red-300/70 hover:text-red-300" data-testid={`training-del-${ex.id}`} aria-label="delete">
                    <Trash size={14} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* === Training Ground 2.0: stem library + AI test generations === */}
        <section className="lg:col-span-3 mt-2 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <StemLibrary />
          <TestGenerationsPanel />
        </section>

        {/* === Drum Kits + Bass Kits: drag-drop sample managers ============== */}
        {/* default kits apply to GenPlayer (Training Lab + Landing) and the DAW */}
        <section className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DrumKitsPanel />
          <BassKitsPanel />
        </section>

        {/* === Melody Kits: per-instrument single-note sample slots that the */}
        {/* AI lead routes through (priority: piano → lead → brass → strings → flute) */}
        <section className="lg:col-span-3">
          <MelodyKitsPanel />
        </section>

        {/* === Image-to-MIDI: admin drops a piano-roll screenshot, vision LLM */}
        {/* returns a structured MIDI buffer the admin can audition. */}
        <section className="lg:col-span-3">
          <ImageToMidiPanel />
        </section>
      </main>
    </div>
  );
}

// ----- Stem library: drag-drop audio files, label them, persist -----
function StemLibrary() {
  const [stems, setStems] = useState([]);
  const [label, setLabel] = useState("kick");
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { API.get("/training/stems").then((r) => setStems(r.data.stems || [])).catch(() => {}); }, []);

  const onFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        // Persist as a blob URL — fine for dev/admin since stems live in-browser.
        // In production swap for an upload-to-S3 step.
        const url = URL.createObjectURL(f);
        const displayName = name.trim() || f.name.replace(/\.[^.]+$/, "");
        const { data } = await API.post("/training/stems", { label, name: displayName, url, tag: tag.trim() });
        setStems((prev) => [data.stem, ...prev]);
      }
    } finally {
      setBusy(false);
      setName(""); setTag("");
    }
  };

  const remove = async (id) => {
    await API.delete(`/training/stems/${id}`);
    setStems((prev) => prev.filter((s) => s.id !== id));
  };

  return (
    <div className="rounded-2xl p-5 space-y-4" style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }} data-testid="stem-library">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2"><Tag size={14} weight="bold" /> Stem library</h2>
        <span className="text-[10px] font-mono text-white/40">{stems.length} stems</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <select value={label} onChange={(e) => setLabel(e.target.value)} className="px-2 py-2 rounded-md bg-[#0a0a0a] border border-[#262626] text-sm" data-testid="stem-label">
          {["kick","snare","hihat","clap","bass","melody","chord","fx","vocal","perc"].map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="display name" className="px-2 py-2 rounded-md bg-[#0a0a0a] border border-[#262626] text-sm" data-testid="stem-name" />
        <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="tag (optional)" className="px-2 py-2 rounded-md bg-[#0a0a0a] border border-[#262626] text-sm" data-testid="stem-tag" />
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = "#34d399"; }}
        onDragLeave={(e) => { e.currentTarget.style.borderColor = "#1f1f1f"; }}
        onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = "#1f1f1f"; onFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="rounded-lg p-6 text-center cursor-pointer transition"
        style={{ background: "#070707", border: "1.5px dashed #1f1f1f" }}
        data-testid="stem-dropzone"
      >
        <UploadSimple size={20} weight="bold" className="mx-auto text-white/40" />
        <div className="text-xs font-mono text-white/50 mt-2">drop audio files here · or click to browse</div>
        <input ref={inputRef} type="file" accept="audio/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="stem-file-input" />
      </div>
      {busy && <div className="text-[10px] text-emerald-300 font-mono">uploading…</div>}
      <ul className="space-y-1 max-h-72 overflow-y-auto pr-1">
        {stems.map((s) => (
          <li key={s.id} className="flex items-center gap-2 rounded-md px-2 py-1.5" style={{ background: "#0a0a0a", border: "1px solid #1a1a1a" }} data-testid={`stem-row-${s.id}`}>
            <span className="text-[10px] font-mono text-white/40 uppercase w-12">{s.label}</span>
            <span className="text-sm flex-1 truncate">{s.name}</span>
            {s.tag && <span className="text-[10px] font-mono text-emerald-300/80">#{s.tag}</span>}
            <button onClick={() => remove(s.id)} className="text-red-300/70 hover:text-red-300" aria-label="delete"><Trash size={12} weight="bold" /></button>
          </li>
        ))}
        {stems.length === 0 && <li className="text-xs text-white/30 font-mono py-4 text-center">no stems yet — drop a kick to start.</li>}
      </ul>
    </div>
  );
}

// ----- Test generations: run AI, audition, approve / disapprove -----
function TestGenerationsPanel() {
  const [tests, setTests] = useState([]);
  const [genre, setGenre] = useState("pop");
  const [bpm, setBpm] = useState(92);
  const [busy, setBusy] = useState(false);
  const [playingId, setPlayingId] = useState(null);

  const load = () => API.get("/training/test-generations").then((r) => setTests(r.data.test_generations || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/training/test-generate", { genre, bpm });
      setTests((prev) => [data.test_generation, ...prev]);
    } finally { setBusy(false); }
  };

  const decide = async (id, decision) => {
    await API.post(`/training/test-generations/${id}/decide`, { decision });
    setTests((prev) => prev.map((t) => t.id === id ? { ...t, status: decision } : t));
  };

  const playPattern = async (t) => {
    if (playingId === t.id) { genPlayer.stop(); setPlayingId(null); return; }
    genPlayer.stop();
    await genPlayer.loadPattern(t.pattern, t.bpm || 120);
    await genPlayer.play();
    setPlayingId(t.id);
  };

  return (
    <div className="rounded-2xl p-5 space-y-4" style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }} data-testid="testgen-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2"><Sparkle size={14} weight="fill" /> Test generations</h2>
        <span className="text-[10px] font-mono text-white/40">{tests.length}</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <select value={genre} onChange={(e) => setGenre(e.target.value)} className="px-2 py-2 rounded-md bg-[#0a0a0a] border border-[#262626] text-sm" data-testid="testgen-genre">
          {["pop","trap","rnb","country","house","afro"].map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <input type="number" value={bpm} onChange={(e) => setBpm(Number(e.target.value))} min={40} max={220} className="px-2 py-2 rounded-md bg-[#0a0a0a] border border-[#262626] text-sm" data-testid="testgen-bpm" />
        <button onClick={run} disabled={busy} className="px-3 py-2 rounded-md text-sm font-bold" style={{ background: "linear-gradient(180deg,#fff,#d4d4d4)", color: "#000" }} data-testid="testgen-run">
          {busy ? <SpinnerGap size={12} weight="bold" className="inline animate-spin mr-1" /> : <Sparkle size={12} weight="fill" className="inline mr-1" />}
          {busy ? "running…" : "Test gen"}
        </button>
      </div>
      <ul className="space-y-2 max-h-[26rem] overflow-y-auto pr-1">
        {tests.map((t) => (
          <li key={t.id} className="rounded-md p-3" style={{ background: "#0a0a0a", border: `1px solid ${t.status === "approve" ? "#10b98155" : t.status === "disapprove" ? "#ef444455" : "#1a1a1a"}` }} data-testid={`testgen-row-${t.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold capitalize">{t.genre} <span className="text-white/40 text-xs font-mono">· {t.bpm} BPM</span></div>
              <div className="text-[10px] font-mono uppercase tracking-wider"
                   style={{ color: t.status === "approve" ? "#34d399" : t.status === "disapprove" ? "#fca5a5" : "#fbbf24" }}>
                {t.status}
              </div>
            </div>
            <div className="text-[10px] font-mono text-white/40 mb-2">
              drums: {Object.values(t.pattern?.drums || {}).map((arr) => arr?.filter?.(Boolean).length ?? 0).join("/")} ·
              bass: {(t.pattern?.bass || []).length} · melody: {(t.pattern?.melody || []).length}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => playPattern(t)} className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1" style={{ background: "#0a0a0a", border: "1px solid #262626" }} data-testid={`testgen-play-${t.id}`}>
                {playingId === t.id ? <><Pause size={10} weight="fill" /> stop</> : <><Play size={10} weight="fill" /> play</>}
              </button>
              <button
                onClick={() => {
                  sessionStorage.setItem("crow_incoming_pattern", JSON.stringify({
                    pattern: t.pattern, bpm: t.bpm, source_test_id: t.id, genre: t.genre,
                    // Carry any previously-saved instrument state so a
                    // reopened Edit-in-DAW session restores the sampler /
                    // synth / FX picks the admin last saved back.
                    instrument_state: t.instrument_state || null,
                  }));
                  window.location.href = "/daw";
                }}
                className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1"
                style={{ background: "#0a0a0a", border: "1px solid #60a5fa55", color: "#93c5fd" }}
                data-testid={`testgen-edit-${t.id}`}
                title="Open this beat in the C.R.O.W. DAW to perfect it before approving"
              >
                ✎ Edit in DAW
              </button>
              <button
                onClick={() => decide(t.id, "approve")}
                className="px-4 py-1.5 rounded-md text-sm font-extrabold flex items-center gap-1.5 transition"
                style={{ background: "linear-gradient(180deg,#10b981,#059669)", color: "#04140d", boxShadow: "0 0 14px rgba(16,185,129,0.45)" }}
                data-testid={`testgen-approve-${t.id}`}
              >
                <ThumbsUp size={14} weight="fill" /> Approve
              </button>
              <button
                onClick={() => decide(t.id, "disapprove")}
                className="px-4 py-1.5 rounded-md text-sm font-extrabold flex items-center gap-1.5 transition"
                style={{ background: "linear-gradient(180deg,#ef4444,#b91c1c)", color: "#1a0606", boxShadow: "0 0 14px rgba(239,68,68,0.4)" }}
                data-testid={`testgen-disapprove-${t.id}`}
              >
                <ThumbsDown size={14} weight="fill" /> Disapprove
              </button>
            </div>
          </li>
        ))}
        {tests.length === 0 && <li className="text-xs text-white/30 font-mono py-4 text-center">no test runs yet.</li>}
      </ul>
      <div className="text-[10px] text-white/40 font-mono p-2 rounded-md" style={{ background: "#070707", border: "1px dashed #1f1f1f" }}>
        💡 Approved tests feed into the curated corpus that the public generator draws inspiration from.
      </div>
    </div>
  );
}
